/**
 * Minimal shape of a cast, owned locally.
 *
 * Previously imported from `@neynar/nodejs-sdk/build/api`, which was never a
 * declared dependency of this package — it resolved only through the old
 * workspace root and broke standalone installs.
 *
 * These are Farcaster's own field names, not Neynar's, because the API now
 * reads casts from Farcaster's public endpoint rather than through a key. The
 * two disagree on nearly every name here — `displayName` against `display_name`,
 * a nested `pfp.url` against a flat `pfp_url`, epoch milliseconds against an ISO
 * string — so this type had been quietly describing a response nobody sends,
 * and every cast ad lost its author's picture to it.
 */
export type Cast = {
  hash: string;
  text: string;
  /** Epoch milliseconds. */
  timestamp: number;
  author: {
    fid: number;
    username?: string;
    displayName?: string;
    pfp?: { url?: string };
  };
  /**
   * What was attached to the cast, grouped by kind.
   *
   * An object, not an array — it was typed `unknown[]` here, which is what a
   * name like "embeds" suggests and not what Farcaster returns. The real shape
   * is `{ images, urls, videos, casts, … }`, so anything that trusted the array
   * type found nothing and moved on without an error.
   *
   * Only `images` is described, because it is the only one drawn. The rest are
   * left loose rather than guessed at.
   */
  embeds?: {
    images?: {
      url?: string;
      sourceUrl?: string;
      alt?: string;
      /** `staticRaster` is a still — the frame to use when the source moves. */
      media?: { staticRaster?: string; width?: number; height?: number };
    }[];
    [kind: string]: unknown;
  };
  [key: string]: unknown;
};

export type CastMetadata = { cast: Cast };

/**
 * The picture a cast carries, or null.
 *
 * One definition because two things need the same answer and must not disagree:
 * the metadata stored when an ad is published, and the card renderer, which
 * re-resolves a cast from its hash rather than reading what was stored. Two
 * copies of "first image, unless it moves" would drift the first time either
 * was touched, and the drift would show as a card that quietly loses its
 * picture.
 *
 * The first attachment only. A cast can carry several and the card has one
 * place to put one; showing the first matches what every Farcaster client does
 * with a collapsed preview.
 */
export function castImage(cast: Pick<Cast, "embeds">): string | null {
  const first = cast.embeds?.images?.[0];
  if (!first) return null;
  // The still before the source: satori draws a single frame either way, and
  // asking an animation to be one is more predictable than taking whichever
  // frame the decoder happens to stop on.
  return first.media?.staticRaster || first.url || first.sourceUrl || null;
}

/**
 * One post from X, as the API's syndication read returns it.
 *
 * Less than a post carries — no thread, no quote, no video — because an ad is a
 * card pointing at the real thing. What is here is what a card shows: who wrote
 * it, what it says, when, and a still of whatever was attached.
 */
export type XPostMedia = {
  /** `photo`, `video` or `animated_gif`, as X names them. */
  type: string;
  /** The picture, or a video's poster frame. Always a still. */
  url: string;
  width: number | null;
  height: number | null;
  alt: string | null;
};

export type TweetMetadata = {
  id: string;
  /** The canonical permalink, rebuilt from the post rather than the paste. */
  url: string;
  /** The whole post, including the body of one longer than 280 characters. */
  text: string;
  author: {
    name: string;
    username: string;
    pfpUrl: string;
  };
  /** ISO 8601. */
  createdAt: string;
  /** Every attachment, in the order the post shows them. Up to four. */
  media: XPostMedia[];
  /** The first attachment's still. Redundant with `media[0]`, kept for ads
   * already pinned before `media` existed. */
  image: string | null;
  metrics: { likes: number; replies: number };
};

/**
 * An X account, as the API returned it at the moment the ad was made.
 *
 * `followers` is the field this type exists for. A profile ad is a claim about
 * an audience, and the number is the only part of that claim a reader can weigh
 * — which is also why it is a snapshot rather than live: it is stored with the
 * ad, so it says what was true when the space was bought, not what is true now.
 */
export type XProfileMetadata = {
  username: string;
  name: string;
  url: string;
  bio: string | null;
  pfpUrl: string | null;
  bannerUrl: string | null;
  followers: number;
  following: number;
  posts: number;
  verified: boolean;
  /** ISO 8601. */
  createdAt: string | null;
};

export type MiniappMetadata = {
  title: string;
  icon: string;
  description: string;
  imageUrl: string;
  /** Whoever signed the manifest, resolved from the fid it carries. */
  creator: {
    fid: number;
    username: string | null;
    displayName: string | null;
    pfpUrl: string | null;
  } | null;
};

export type LinkMetadata = {
  title: string;
  description: string;
  image: string;
  icon: string;
};

export type ProfileMetadata = {
  pfpUrl: string;
  bio: string;
  username: string;
  displayName?: string;
  followers: number;
  following: number;
  pro: boolean;
};

export type TokenMetadata = {
  name: string;
  symbol: string;
  decimals: number;
  logoURI: string;
};
